import { useState } from "react";
import { clsx } from "clsx";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useManagedFileOpener } from "@/hooks/useManagedFileOpener";
import { isPlainLeftClick } from "@/utils/managedFile";
import { useOwnerAccessMutations } from "../hooks";
import type { ManagedFileMini, OwnerAccess } from "../types";
import { apiErr } from "../utils";

// Dokumenty tytułu właściciela (zakładka „Właściciele" w panelu działki): każdy z koszem,
// usunięcie po potwierdzeniu w modalu. Ostatni dokument tytułu „Pozyskany" cofa
// pozyskanie — backend wraca tytuł na „Nie rozpoczęto", a modal to zapowiada.
//
// „Nieaktualny": tytuł pozyskany na STAREJ wersji trasy (`access.is_stale`, liczone przez
// backend przy każdym odczycie) barwi dokumenty na bursztynowo i znaczy chipem. Flaga jest
// pochodną tytułu, nie pliku — gdy trasa wróci (rozciągnięcie tytułu na aktualną wersję,
// ręczne albo z porównania przebiegu), oznaczenie znika samo.

const STALE_TITLE =
  "Tytuł pozyskany na nieaktualnej wersji trasy — dokument do weryfikacji. Po rozciągnięciu tytułu na aktualną trasę oznaczenie zniknie.";

export function OwnerTitleDocuments({ access, connectionId }: { access: OwnerAccess; connectionId: string }) {
  const m = useOwnerAccessMutations(connectionId);
  const { openFile } = useManagedFileOpener();
  const [pending, setPending] = useState<ManagedFileMini | null>(null);
  const stale = access.is_stale;
  const revertsAcquisition = access.status === "secured" && access.files.length === 1;

  const confirmRemove = () => {
    if (!pending) return;
    m.removeFile.mutate({ id: access.id, fileId: pending.id }, { onSuccess: () => setPending(null) });
  };

  return (
    <>
      <div className="flex flex-wrap gap-1.5" data-testid="owner-title-documents">
        {access.files.map((f) => (
          <span
            key={f.id}
            data-stale={stale ? "true" : undefined}
            className={clsx(
              "inline-flex min-w-0 items-center gap-1 px-1.5 py-0.5 text-[11px] font-mono border",
              stale
                ? "border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/15 dark:text-amber-300"
                : "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-primary)]",
            )}
          >
            {f.url ? (
              <a
                href={f.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  if (!isPlainLeftClick(e)) return;
                  e.preventDefault();
                  void openFile(f);
                }}
                title={f.original_filename}
                className="truncate max-w-[220px] hover:underline"
              >
                {f.original_filename}
              </a>
            ) : (
              <span className="truncate max-w-[220px]" title={f.original_filename}>{f.original_filename}</span>
            )}
            {stale && (
              <span
                className="shrink-0 text-[9px] uppercase tracking-wide px-1 border border-amber-400 dark:border-amber-500/60"
                title={STALE_TITLE}
              >
                nieaktualny
              </span>
            )}
            <button
              type="button"
              className="inline-flex shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] transition-colors"
              aria-label={`Usuń dokument ${f.original_filename}`}
              title="Usuń dokument"
              onClick={() => setPending(f)}
            >
              <Trash2 size={11} />
            </button>
          </span>
        ))}
      </div>

      <Modal open={pending !== null} onClose={() => setPending(null)} title="Usunąć dokument?" size="sm">
        <div className="space-y-3 text-sm p-4">
          <p>
            Dokument <b>{pending?.original_filename}</b> zostanie odpięty od tytułu właściciela.
          </p>
          {revertsAcquisition && (
            <p className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                To ostatni dokument tytułu „Pozyskany” — pozyskanie zostanie cofnięte, a tytuł wróci na
                „Nie rozpoczęto”.
              </span>
            </p>
          )}
          {m.removeFile.error && <p className="text-[var(--color-danger)]">{apiErr(m.removeFile.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setPending(null)}>Anuluj</Button>
            <Button size="sm" variant="danger" loading={m.removeFile.isPending} onClick={confirmRemove}>
              <Trash2 size={12} /> Usuń
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
